/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { formatDate } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';

export const MAT_TEMPORAL_DATE_ADAPTER_OPTIONS: InjectionToken<ITemporalDateAdapterOptions> = new InjectionToken(
    'MAT_TEMPORAL_DATE_ADAPTER_OPTIONS',
    {
        providedIn: 'root',
        factory: MAT_TEMPORAL_DATE_ADAPTER_OPTIONS_FACTORY,
    },
);

export function MAT_TEMPORAL_DATE_ADAPTER_OPTIONS_FACTORY(): ITemporalDateAdapterOptions {
    return {
        outputFormat: TemporalDateAdapterFormat.TEMPORAL,
    };
}

export interface ITemporalDateFormat {
    parse: {
        dateInput: Intl.DateTimeFormatOptions | string;
    };
    display: {
        dateInput: Intl.DateTimeFormatOptions | string;
        monthLabel?: Intl.DateTimeFormatOptions | string;
        monthYearLabel: Intl.DateTimeFormatOptions | string;
        dateA11yLabel: Intl.DateTimeFormatOptions | string;
        monthYearA11yLabel: Intl.DateTimeFormatOptions | string;
    };
}

export type TemporalDateType = Temporal.PlainDate | string | number;

export enum TemporalDateAdapterFormat {
    NUMBER = 'NUMBER',
    TEMPORAL = 'TEMPORAL'
}

export interface ITemporalDateAdapterOptions {
    outputFormat?: TemporalDateAdapterFormat | string;
    timezone?: string;
    firstDayOfWeek?: number;
}


export function formatTemporal(
    date: Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime,
    displayFormat: Intl.DateTimeFormatOptions | string,
    locale: string = 'en',
    timezone?: string,
): string {
    if (!date || !displayFormat) {
        return null;
    }
    if (typeof displayFormat === 'string') {
        if (date instanceof Temporal.PlainDate) {
            return formatDate(Date.UTC(date.year, date.month - 1, date.day), displayFormat, locale, 'UTC');
        } else if (date instanceof Temporal.PlainDateTime) {
            return formatDate(Date.UTC(date.year, date.month - 1, date.day, date.hour, date.minute, date.second, date.millisecond), displayFormat, locale, 'UTC');
        } else {
            return formatDate(date.toString().split('[')[ 0 ], displayFormat, locale, timezone);
        }
    } else {
        if (date instanceof Temporal.ZonedDateTime) {
            return date.withTimeZone(timezone).toLocaleString(locale, displayFormat);
        } else {
            return date.toLocaleString(locale, displayFormat);
        }
    }
}
// Remove namespace for developement;

declare let TemporalParser;
declare let Temporal;
window[ 'TemporalParser' ] = window[ 'TemporalParser' ] || undefined;
window[ 'Temporal' ] = window[ 'Temporal' ] || undefined;

// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace Temporal {
    // eslint-disable-next-line @typescript-eslint/naming-convention,@typescript-eslint/ban-types
    export interface PlainDate extends Object {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [ x: string | number | symbol ]: any;

        toLocaleString: (...args) => string;
        toString: (...args) => string;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention,@typescript-eslint/ban-types
    export interface PlainDateTime extends Object {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [ x: string | number | symbol ]: any;

        toLocaleString: (...args) => string;
        toString: (...args) => string;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention,@typescript-eslint/ban-types
    export interface ZonedDateTime extends Object {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [ x: string | number | symbol ]: any;
        withTimeZone: (...args) => Temporal.ZonedDateTime;
        toLocaleString: (...args) => string;
        toString: (...args) => string;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    export type PlainDateLike = Record<string | number | symbol, any>;
}


/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
    const valuesArray = Array(length);
    for (let i = 0; i < length; i++) {
        valuesArray[ i ] = valueFunction(i);
    }
    return valuesArray;
}

/** Adapts the native JS Date for use with cdk-based components that work with dates. */
@Injectable()
export class TemporalDateAdapter extends DateAdapter<TemporalDateType> {
    /** The injected locale. */
    private readonly _matDateLocale: string = inject(MAT_DATE_LOCALE, { optional: true }) as string;
    private readonly _options: ITemporalDateAdapterOptions = inject(MAT_TEMPORAL_DATE_ADAPTER_OPTIONS);

    private options: ITemporalDateAdapterOptions;
    private timezone?: string;
    constructor(

    ) {
        super();
        super.setLocale(this._matDateLocale);
        this.timezone = this._options.timezone;
        this.options = { ...this._options, outputFormat: this._options.outputFormat || TemporalDateAdapterFormat.TEMPORAL };
        if (!TemporalParser) {
            console.warn(`
            Please add any parsers to be able to parse complex date inputs:

            Github: https://github.com/kensnyder/any-date-parser


            polyfill.ts:

                import * as parser from 'any-date-parser'

                declare global {
                    var TemporalParser: typeof parser;
                    
                }
                window.TemporalParser = parser;`);
        }
        if (!Temporal) {
            console.error(`
            Please add Temporal Polyfill:

            Github: https://github.com/fullcalendar/temporal-polyfill


            polyfill.ts:

                import 'temporal-polyfill/global';

            `);
        }
    }
    public setLocale(locale: string): void {
        super.setLocale(locale);
        this._localeChanges.next();
    }

    public setTimezone(timezone: string): void {
        this.timezone = timezone;
        this._localeChanges.next();
    }

    public getYear(date: TemporalDateType): number {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.year;
    }

    public getMonth(date: TemporalDateType): number {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.month - 1;
    }

    public getDate(date: TemporalDateType): number {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.day;
    }

    public getDayOfWeek(date: TemporalDateType): number {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.dayOfWeek;
    }

    public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
        return [
            ...range(12, (i) =>
                Temporal.PlainDate.from({
                    year: 2017,
                    month: i + 1,
                    day: 1,
                }).toLocaleString(this.locale, { month: style })
            ),
        ];
    }

    public getDateNames(): string[] {
        return range(31, (i) =>
            Temporal.PlainDate.from({
                year: 2017,
                month: 1,
                day: i + 1,
            }).toLocaleString(this.locale, { day: 'numeric' })
        );
    }

    public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
        return range(7, (i) =>
            Temporal.PlainDate.from({
                year: 2017,
                month: 1,
                day: i + 1,
            }).toLocaleString(this.locale, { weekday: style })
        );
    }

    public getYearName(date: TemporalDateType): string {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.toLocaleString(this.locale, { year: 'numeric' });
    }

    public getFirstDayOfWeek(): number {
        return this.options.firstDayOfWeek || 0;
    }

    public getNumDaysInMonth(date: TemporalDateType): number {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.daysInMonth;
    }

    public clone(date: TemporalDateType): TemporalDateType {
        if (date instanceof Temporal.PlainDate) {
            return Temporal.PlainDate.from(date);
        } else {
            return date;
        }
    }

    public createDate(
        year: number,
        month: number,
        date: number
    ): TemporalDateType {
        const result: Temporal.PlainDate = Temporal.PlainDate.from({
            year,
            month: month + 1,
            day: date,
        });
        // Check that the date wasn't above the upper bound for the month, causing the month to overflow
        if (result.month !== month + 1) {
            throw Error(`Invalid date "${date}" for month with index "${month}".`);
        }
        if (this.options.outputFormat === 'TEMPORAL') {
            return result;
        } else if (this.options.outputFormat === 'NUMBER') {
            return Date.UTC(result.year, result.month - 1, result.day);
        } else if (this.options.outputFormat) {
            return formatDate(Date.UTC(result.year, result.month - 1, result.day), this.options.outputFormat, this.locale, 'UTC');
        } else {
            return result;
        }
    }

    public today(): TemporalDateType {
        const today: Temporal.PlainDate = Temporal.Now.plainDateISO(this.timezone);
        return this.createDate(today.year, today.month - 1, today.day);
    }

    public parse(value: unknown, parseFormat: Intl.DateTimeFormatOptions | string): TemporalDateType | null {
        return value ? this.parseFormattedDate(value as string, parseFormat) : null;
    }

    public format(date: TemporalDateType, displayFormat: Intl.DateTimeFormatOptions | string): string {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        if (!this.isValid(temporalDate)) {
            throw Error('NativeDateAdapter: Cannot format invalid date.');
        }
        return formatTemporal(temporalDate, displayFormat, this.locale);
        // return temporalDate.toLocaleString(this.locale, displayFormat);
    }

    public addCalendarYears(
        date: TemporalDateType,
        years: number
    ): TemporalDateType {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date).add({ years });
        return this.createDate(temporalDate.year, temporalDate.month - 1, temporalDate.day);
    }

    public addCalendarMonths(
        date: TemporalDateType,
        months: number
    ): TemporalDateType {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date).add({ months });
        return this.createDate(temporalDate.year, temporalDate.month - 1, temporalDate.day);
    }

    public addCalendarDays(
        date: TemporalDateType,
        days: number
    ): TemporalDateType {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date).add({ days });
        return this.createDate(temporalDate.year, temporalDate.month - 1, temporalDate.day);
    }

    public toIso8601(date: TemporalDateType): string {
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(date);
        return temporalDate.toString({ calendarName: 'never' });
    }

    /**
     * Returns the given value if given a valid Date or null. Deserializes valid ISO 8601 strings
     * (https://www.ietf.org/rfc/rfc3339.txt) into valid Dates and empty string into null. Returns an
     * invalid date for all other values.
     */
    public override deserialize(value: unknown): TemporalDateType | null {
        if (!value) {
            return null;
        }
        const temporalDate: Temporal.PlainDate = this.createTemporalDate(value as TemporalDateType);
        return this.createDate(temporalDate.year, temporalDate.month - 1, temporalDate.day);
    }

    public isDateInstance(value: TemporalDateType): boolean {
        if (this.options.outputFormat === 'TEMPORAL') {
            return value instanceof Temporal.PlainDate;
        } else if (this.options.outputFormat === 'NUMBER') {
            return typeof value === 'number';
        } else {
            return typeof value === 'string';
        }
    }

    public isValid(date: Temporal.PlainDate): boolean {
        try {
            date.toString();
            return true;
        } catch (e) {
            return false;
        }
    }

    public invalid(): string {
        return 'invalid';
    }

    private createTemporalDate(date: TemporalDateType): Temporal.PlainDate {
        if (date instanceof Temporal.PlainDate) {
            return Temporal.PlainDate.from(date);
        } else if (typeof date === 'string' && typeof this.options.outputFormat === 'string' ) {
            return this.parseOutputFormatString(date, this.options.outputFormat );
        } else if (typeof date === 'number') {
            return Temporal.Instant.fromEpochMilliseconds( date ).toZonedDateTimeISO('UTC').toPlainDate();
        } else {
            return Temporal.PlainDate.from(date);
        }
    }
    private getDateFormat(parseFormat: Intl.DateTimeFormatOptions | string): string {
        if (typeof parseFormat === 'string') {
            return parseFormat;
        } else {
            const formatted: string = this.format(this.createTemporalDate(this.createDate(2000, 0, 2)), parseFormat);
            return formatted
                .replace('2000', 'yyyy')
                .replace('01', 'MM')
                .replace('1', 'M')
                .replace('02', 'dd')
                .replace('2', 'd');
        }
    }

    private getFormattedDateRegex(format: string): RegExp {
        try {
            return new RegExp(
                `^\\s*${  format.replace(/([Mdy])\1*/g, '?(?<$1>\\d+)?')  }\\s*$`
            );
        } catch (e) {
            return new RegExp('$^');
        }
    }

    private parseFormattedDate(dateString: string, parseFormat: Intl.DateTimeFormatOptions | string): TemporalDateType | null {
        const format: string = this.getDateFormat(parseFormat);
        const regex: RegExp = this.getFormattedDateRegex(format);

        let part: number = 0;
        const value = format?.split('')?.reduce((prev, curr, i) => {
            if (!'abcdefghijklmnopqrstuvwxyz'.includes(curr?.toLocaleLowerCase())) {
                part = format?.slice(0, i + 1)?.split('')?.filter((v) => v === curr)?.length - 1 || 0;
                const parts = prev.split(curr);
                if (parts[ part ]?.length < format.split(curr)[ part ]?.length && parts[ part ]?.length) {
                    parts[ part ] = parts[ part ].padStart(format.split(curr)[ part ]?.length, '0');
                    prev = parts.join(curr);
                }
                if (prev[ i ] !== curr && !!prev[ i ]) {
                    return prev.slice(0, i) + curr + prev.slice(i);
                }
            }
            return prev;
        }, dateString) || dateString;

        const result: Temporal.PlainDateLike = TemporalParser?.attempt(value, this.locale);
        let temporalDate: Temporal.PlainDate;
        const today: Temporal.PlainDate = Temporal.Now.plainDateISO(this.timezone);
        if (result?.day || result?.month || result?.year) {
            temporalDate = Temporal.PlainDate.from({
                year: result.year || today.year,
                month: result.month || today.month,
                day: result.day || today.day,
            });
        } else {
            const { groups } = value.match(regex) ?? {};
            if (!groups?.d && !groups?.M && !groups?.y || groups.d === '0' || groups.M === '0' || groups.y === '0') {
                return null;
            }
            const yearCount: number = format.split('').filter((v: string) => v === 'y')?.length;
            const monthCount: number = format.split('').filter((v: string) => v === 'M')?.length;
            const dayCount: number = format.split('').filter((v: string) => v === 'd')?.length;
            if (!!groups.d && groups.d.length !== dayCount && value.length < dayCount) {
                groups.d = value;
            }
            const yearString: string = (groups.y || today.year).toString().padStart(yearCount, today.year?.toString()).slice(0, yearCount);
            const y = Number(yearString);
            const m = Math.min(12, Number((groups.M || today.month).toString().padStart(monthCount, '0').slice(-monthCount)));
            const d =  Math.min(31, Number((groups.d || today.day).toString().padStart(dayCount, '0').slice(-dayCount)));
            temporalDate = Temporal.PlainDate.from({
                year: y,
                month: m,
                day: d,
            });
        }
        return this.createDate(temporalDate.year, temporalDate.month - 1, temporalDate.day);

    }

    private parseOutputFormatString(value: string, format: string): Temporal.PlainDate  {
        const regex: RegExp = this.getFormattedDateRegex(format);
        let { groups } = value.match(regex) ?? {};
        groups = groups || {};
        const yearCount: number = 4;
        const monthCount: number = 2;
        const dayCount: number = 2;
        let y; let m; let d;
        if (groups.y?.length !== yearCount || groups.M?.length !== monthCount) {
            const today: Temporal.PlainDate = Temporal.Now.plainDateISO(this.timezone);
            y = Number((groups.y || today.year).toString().padStart(yearCount, today.year?.toString()).slice(0, yearCount));
            m = Math.min(12, Number((groups.M || today.month).toString().padStart(monthCount, '0').slice(-monthCount)));
            d =  Math.min(31, Number(groups.d.toString().padStart(dayCount, '0').slice(-dayCount)));
        } else {
            y = groups.y;
            m = Math.min(12, +groups.M);
            d =  Math.min(31, Number(groups.d.toString().padStart(dayCount, '0').slice(-dayCount)));
        }
        return Temporal.PlainDate.from({
            year: y,
            month: m,
            day: d,
        });
    }

}
